import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Target, Layers, MessageCircle, BookOpen, Zap, Image, Mic, Move, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { api, storage } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";

const AREA_ICONS = {
  1: Target,
  2: Layers,
  3: MessageCircle,
  4: BookOpen,
  5: Zap,
  6: Image,
  7: Mic,
  8: Move,
};

export default function Assessment() {
  const navigate = useNavigate();
  const { step } = useParams();
  const { lang, lp, t } = useLang();
  const currentStep = parseInt(step) || 1;

  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState({});
  const [importance, setImportance] = useState({});
  const [userId, setUserId] = useState(null);

  const currentArea = areas[currentStep - 1];

  useEffect(() => {
    const user = storage.getUser();
    if (!user) {
      navigate(lp("/profile"));
      return;
    }
    setUserId(user.id);

    api.getSeedData(lang).then(data => {
      setAreas(data.areas);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      toast.error(t.assessment.errLoad);
    });

    const progress = storage.getAssessmentProgress();
    if (progress && progress.userId === user.id) {
      setAnswers(progress.answers || {});
      setNotes(progress.notes || {});
      setImportance(progress.importance || {});
    }
  }, [navigate, lang]);

  useEffect(() => {
    if (userId && Object.keys(answers).length > 0) {
      storage.setAssessmentProgress({
        userId: userId,
        currentStep,
        answers,
        notes,
        importance,
      });
    }
  }, [answers, notes, importance, currentStep, userId]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value[0] }));
  };

  const handleNotesChange = (areaId, value) => {
    setNotes(prev => ({ ...prev, [areaId]: value }));
  };

  const handleImportanceChange = (areaId, value) => {
    setImportance(prev => ({ ...prev, [areaId]: value[0] }));
  };

  const getAreaScore = (area) => {
    if (!area) return 0;
    const areaAnswers = area.questions.map(q => answers[q.id]).filter(a => a !== undefined);
    if (areaAnswers.length === 0) return 0;
    return (areaAnswers.reduce((sum, val) => sum + val, 0) / areaAnswers.length).toFixed(1);
  };

  const isStepComplete = () => {
    if (!currentArea) return false;
    return currentArea.questions.every(q => answers[q.id] !== undefined);
  };

  const handleNext = () => {
    if (!isStepComplete()) {
      toast.error(t.assessment.errAnswerAll);
      return;
    }

    if (currentStep < 8) {
      navigate(lp(`/assessment/${currentStep + 1}`));
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      navigate(lp(`/assessment/${currentStep - 1}`));
    } else {
      navigate(lp("/profile"));
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const areaScores = areas.map(area => {
        const areaAnswers = area.questions.map(q => ({
          question_id: q.id,
          score: answers[q.id] || 5,
        }));
        const avgScore = areaAnswers.reduce((sum, a) => sum + a.score, 0) / areaAnswers.length;

        return {
          area_id: area.id,
          area_name: area.name,
          answers: areaAnswers,
          average_score: parseFloat(avgScore.toFixed(1)),
          notes: notes[area.id] || null,
          importance: importance[area.id] || 3,
        };
      });

      const assessment = await api.createAssessment({
        user_id: userId,
        area_scores: areaScores,
        lang,
      });

      storage.clearAssessmentProgress();
      toast.success(t.assessment.done);
      navigate(lp(`/results/${assessment.id}`));
    } catch (error) {
      console.error(error);
      toast.error(t.assessment.errSave);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#222222] text-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#FFEA00] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!currentArea) {
    return null;
  }

  const Icon = AREA_ICONS[currentArea.id] || Target;
  const progress = (currentStep / 8) * 100;

  return (
    <div className="min-h-screen bg-[#222222] text-white">
      {/* Header with Progress */}
      <header className="sticky top-0 z-10 bg-[#222222]/95 backdrop-blur-sm border-b border-white/5">
        <div className="px-6 py-4 max-w-3xl mx-auto">
          <div className="flex justify-between items-center mb-3">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-white/60 hover:text-white -ml-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t.common.back}
            </Button>
            <span className="text-sm text-white/60">{t.assessment.stepOf(currentStep)}</span>
          </div>
          <Progress value={progress} className="h-2 bg-white/10" />
        </div>
      </header>

      <main className="px-6 py-8 max-w-3xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Area Header */}
            <div className="mb-8">
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${currentArea.color}20` }}
              >
                <Icon className="w-8 h-8" style={{ color: currentArea.color }} />
              </div>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {currentArea.name}
              </h1>
              <p className="text-white/60">{currentArea.description}</p>
            </div>

            {/* Questions */}
            <div className="space-y-8 mb-8">
              {currentArea.questions.map((question) => (
                <div key={question.id} className="space-y-4">
                  <div className="flex justify-between items-start gap-4">
                    <p className="text-lg">{question.text}</p>
                    <span
                      className="text-2xl font-bold min-w-[40px] text-right score-display"
                      style={{ color: currentArea.color }}
                    >
                      {answers[question.id] || "-"}
                    </span>
                  </div>
                  <div className="pt-2">
                    <Slider
                      data-testid={`slider-${question.id}`}
                      value={[answers[question.id] || 5]}
                      onValueChange={(value) => handleAnswerChange(question.id, value)}
                      min={1}
                      max={10}
                      step={1}
                      className="score-slider"
                    />
                    <div className="flex justify-between text-xs text-white/40 mt-2">
                      <span>{t.assessment.disagree}</span>
                      <span>{t.assessment.agree}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Importance Rating */}
            <div className="mb-8 p-6 rounded-xl bg-[#2a2a2a] border border-white/5">
              <p className="text-lg mb-4">
                {t.assessment.importanceQuestion(currentArea.name)}
              </p>
              <div className="flex justify-between items-center gap-4 mb-2">
                <span className="text-sm text-white/60">{t.assessment.notImportant}</span>
                <span
                  className="text-2xl font-bold score-display"
                  style={{ color: currentArea.color }}
                >
                  {importance[currentArea.id] || 3}
                </span>
                <span className="text-sm text-white/60">{t.assessment.veryImportant}</span>
              </div>
              <Slider
                data-testid={`importance-slider-${currentArea.id}`}
                value={[importance[currentArea.id] || 3]}
                onValueChange={(value) => handleImportanceChange(currentArea.id, value)}
                min={1}
                max={5}
                step={1}
                className="score-slider"
              />
            </div>

            {/* Notes */}
            <div className="mb-8">
              <label className="block text-sm text-white/60 mb-2">
                {t.assessment.notesLabel}
              </label>
              <Textarea
                data-testid={`notes-${currentArea.id}`}
                placeholder={t.assessment.notesPlaceholder(currentArea.name)}
                value={notes[currentArea.id] || ""}
                onChange={(e) => handleNotesChange(currentArea.id, e.target.value)}
                className="bg-[#333] border-white/10 text-white placeholder:text-white/40 focus:border-[#FFEA00] min-h-[100px]"
              />
            </div>

            {/* Current Score Summary */}
            <div className="mb-8 p-4 rounded-xl bg-[#1a1a1a] flex items-center justify-between">
              <span className="text-white/60">{t.assessment.currentScore(currentArea.name)}</span>
              <span
                className="text-3xl font-bold score-display"
                style={{ color: currentArea.color }}
              >
                {getAreaScore(currentArea)}
              </span>
            </div>

            {/* Navigation */}
            <div className="flex gap-4">
              <Button
                onClick={handleNext}
                disabled={submitting}
                data-testid="next-btn"
                className="flex-1 bg-[#FFEA00] hover:bg-[#FFD600] text-black font-semibold py-6 text-lg rounded-lg transition-all hover:scale-[1.02]"
              >
                {submitting ? (
                  t.common.saving
                ) : currentStep === 8 ? (
                  <>
                    {t.assessment.complete}
                    <Save className="ml-2 w-5 h-5" />
                  </>
                ) : (
                  <>
                    {t.assessment.next(areas[currentStep]?.name)}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
